import { MouseEventHandler, ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';

export type ButtonLinkProps = {
  href: string;
  children: ReactNode;
  className?: string;
  target?: string;
  rel?: string;
  /**
   * For a destination that is still a real url but wants to be reached a longer way
   * round — the sale cart's Checkout, which offers an upgrade before it navigates.
   * Keeping the `href` is the point: a middle click, a crawler and a browser with no
   * javascript all still get there.
   */
  onClick?: MouseEventHandler<HTMLAnchorElement>;
};

/**
 * A link that looks like the brand's primary button.
 *
 * `Button` cannot do this job: it renders a `<button>`, and these destinations are real
 * navigations — a `mailto:` on the contact pages, an off-site sign-up on the affiliate
 * page. An anchor is the correct element, and this is the filled counterpart of
 * `SideMenuFooterLink`, which is the outlined one.
 *
 * Four components in this directory sit on the same two axes — what the element is, and
 * what it looks like — and the head noun of each name is the element:
 *
 *     looks like:      a button          a text link
 *     <a>              ButtonLink        Link
 *     <button>         Button            TextButton
 *
 * So `ButtonLink` is a Link and navigates; `TextButton` is a Button and acts on the page
 * it is already on. Reach for this one whenever the answer to "where does the user end
 * up" is a url — including a `mailto:`, which a `<button>` cannot open.
 *
 * Nothing here is brand-specific. `primary` is themed per brand by v2.css, so the same
 * component comes out MagSpot blue, MagZoom indigo and DiscountMags red without being
 * told which site it is on.
 *
 * `no-underline` matters: these buttons sit inside `prose` regions, which underlines
 * links, and a filled button with an underline through it reads as a mistake.
 *
 * The class list is merged rather than concatenated, for the same reason `Price` is: a
 * caller's `className` should replace the default it collides with instead of being
 * appended to it. Concatenated, `rounded` and `rounded-lg` both land on the element and
 * the cascade decides — and the compiled sheet emits `rounded` first, so the default wins
 * and the caller's radius is silently ignored.
 */
export default function ButtonLink({ href, children, className, target, rel, onClick }: ButtonLinkProps) {
  return (
    <a
      href={href}
      target={target}
      rel={rel}
      onClick={onClick}
      className={twMerge(
        'inline-block rounded-lg bg-primary px-5 py-2.5',
        'text-sm font-semibold uppercase tracking-wide text-white no-underline',
        'transition-colors hover:bg-primary-700',
        className
      )}
    >
      {children}
    </a>
  );
}
